import { useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Box,
  Heading,
  Text,
  Badge,
  TableContainer,
} from '@chakra-ui/react';
import { Metric, MetricGoal, User } from '../../types';
import {
  getQuarterWeeks,
  formatDateRange,
  WeekColumn,
} from '../../utils/scorecardCalculations';
import { ScorecardRow } from './ScorecardRow';

interface ScorecardWeeklyGridProps {
  metrics: Metric[];
  metricGoals: MetricGoal[]; // All goals for the displayed metrics
  quarter: number; // 1-4
  year: number;
  onUpdateGoal: (
    metricId: string,
    periodNumber: number,
    value: number | null,
    notes: string,
    dateStart: Date,
    dateEnd: Date,
    goalId?: string
  ) => Promise<void>;
  onDropToIssue: (metric: Metric, week: WeekColumn, actual: number | null, goal: number) => void;
  users: User[];
}

export function ScorecardWeeklyGrid({
  metrics,
  metricGoals,
  quarter,
  year,
  onUpdateGoal,
  onDropToIssue,
  users,
}: ScorecardWeeklyGridProps) {
  // Calculate the 13 weeks for the given quarter
  const weeks = useMemo(() => getQuarterWeeks(quarter, year), [quarter, year]);

  // Group goals by metricId for easier access
  const goalsByMetricId = useMemo(() => {
    const map = new Map<string, MetricGoal[]>();
    metricGoals.forEach((goal) => {
      if (!map.has(goal.metricId)) {
        map.set(goal.metricId, []);
      }
      map.get(goal.metricId)?.push(goal);
    });
    return map;
  }, [metricGoals]);

  // Sort metrics: Display Order first, then Category, then Name
  const sortedMetrics = useMemo(() => {
    return [...metrics].sort((a, b) => {
      // 1. Display Order
      if ((a.displayOrder ?? Infinity) !== (b.displayOrder ?? Infinity)) {
        return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity);
      }
      // 2. Category
      if ((a.category ?? '') !== (b.category ?? '')) {
        return (a.category ?? '').localeCompare(b.category ?? '');
      }
      // 3. Name
      return (a.metricName ?? '').localeCompare(b.metricName ?? '');
    });
  }, [metrics]);

  return (
    <Box overflowX="auto" pb={4}>
      <TableContainer>
        <Table variant="simple" size="sm" border="1px" borderColor="gray.200">
          <Thead bg="gray.50">
            <Tr>
              {/* Sticky Metric Name Column */}
              <Th 
                position="sticky" 
                left={0} 
                bg="gray.50" 
                zIndex={2} 
                width="250px" 
                minW="250px"
                boxShadow="2px 0 5px -2px rgba(0,0,0,0.1)"
              >
                Metric / Measurable
              </Th>
              <Th width="120px" minW="120px">Who</Th>
              <Th textAlign="center" width="80px">Goal</Th>
              {/* 13 Week Columns */}
              {weeks.map((week) => (
                <Th 
                  key={week.weekNumber} 
                  textAlign="center" 
                  minW="60px"
                  bg={week.isReviewWeek ? 'blue.50' : 'gray.50'}
                  borderLeftWidth={week.isReviewWeek ? '2px' : '1px'}
                    borderRightWidth={week.isReviewWeek ? '2px' : '1px'}
                    borderColor={week.isReviewWeek ? 'blue.200' : 'gray.200'}
                >
                  <Box>
                    <Text fontSize="xs" fontWeight="bold">W{week.weekNumber}</Text>
                    <Text fontSize="2xs" color="gray.500" fontWeight="normal">
                      {week.label.split(' ')[1]} {/* Just showing date part e.g. "1-7" */}
                    </Text>
                  </Box>
                </Th>
              ))}
              <Th textAlign="center" width="60px" position="sticky" right={0} bg="gray.50" zIndex={2} boxShadow="-2px 0 5px -2px rgba(0,0,0,0.1)">
                Status
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedMetrics.map((metric) => (
              <ScorecardRow
                key={metric.id}
                metric={metric}
                goals={goalsByMetricId.get(metric.id) || []}
                weeks={weeks}
                onUpdateGoal={async (metricId, goalId, periodNumber, value, notes, dateStart, dateEnd) => {
                  await onUpdateGoal(metricId, periodNumber, value, notes, dateStart, dateEnd, goalId);
                }}
                onDropToIssue={onDropToIssue}
                users={users}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
